import cookies from "js-cookie";
import { create } from "zustand";

import { Currency } from "@/Apollo/schema";
import {
  DISPLAY_CURRENCY_COOKIE,
  DISPLAY_CURRENCY_GUESS_COOKIE,
  toUrlCurrency,
} from "@/features/display-currency/currency-url";
import { toKnownCurrency } from "@/features/display-currency/display-currency.sync";

export { DISPLAY_CURRENCY_COOKIE, DISPLAY_CURRENCY_GUESS_COOKIE };

const COOKIE_OPTIONS = { expires: 365, sameSite: "lax" } as const;

/**
 * A guess is kept for a day (WHO-6199): long enough that the edge sends the visitor to their
 * variant from their second page view on, short enough that a traveller is asked again tomorrow.
 */
const GUESS_COOKIE_OPTIONS = { expires: 1, sameSite: "lax" } as const;

type DisplayCurrencyState = {
  /** The buyer's pick or the account's, kept in the cookie; null when there is none. */
  picked: Currency | null;
  /**
   * The first guess from the visitor's country, for a visitor with no pick. Never saved on the
   * account; a non-euro guess is kept in its own short-lived cookie for the edge (WHO-6199).
   */
  guess: Currency | null;
  /**
   * A currency the buyer picked while signed in, on this page load. It was sent to the account
   * as it was picked, so the account's older value must not win over it at reconciliation,
   * which can come later when the pick beats the `me` query.
   */
  pickedSignedIn: Currency | null;
  /**
   * Read in the browser after mount, so the first render matches the server's. Until then the
   * page renders in the currency the server rendered it in: the address's (WHO-6199), or each
   * listing's own.
   */
  hydrated: boolean;
  hydrate: () => void;
  setGuess: (currency: Currency) => void;
  /** The buyer picked a currency, or the account's is taken over. */
  pick: (currency: Currency) => void;
  /** The buyer picked a currency while signed in; it is being saved on the account. */
  pickSignedIn: (currency: Currency) => void;
};

/**
 * The display currency the web keeps between visits (WHO-5143), the apps' `AppPreferences`: one
 * stored value, the buyer's own. `DisplayCurrencySync` reads it, fetches a first guess when there
 * is none, and reconciles it with the account once per sign-in.
 */
export const useDisplayCurrencyStore = create<DisplayCurrencyState>(set => ({
  picked: null,
  guess: null,
  pickedSignedIn: null,
  hydrated: false,
  hydrate: () =>
    set(state =>
      state.hydrated
        ? state
        : {
            picked: toKnownCurrency(cookies.get(DISPLAY_CURRENCY_COOKIE)),
            /* A kept guess saves asking `/api/display-currency-guess` on every page load. */
            guess: toUrlCurrency(cookies.get(DISPLAY_CURRENCY_GUESS_COOKIE)),
            hydrated: true,
          }
    ),
  setGuess: currency => {
    /* A euro guess is the clean URL: no cookie, so the middleware never runs for it. */
    if (toUrlCurrency(currency)) {
      cookies.set(
        DISPLAY_CURRENCY_GUESS_COOKIE,
        currency,
        GUESS_COOKIE_OPTIONS
      );
    } else {
      cookies.remove(DISPLAY_CURRENCY_GUESS_COOKIE);
    }

    set({ guess: currency });
  },
  pick: currency => {
    cookies.set(DISPLAY_CURRENCY_COOKIE, currency, COOKIE_OPTIONS);
    set({ picked: currency });
  },
  pickSignedIn: currency => {
    cookies.set(DISPLAY_CURRENCY_COOKIE, currency, COOKIE_OPTIONS);
    set({ picked: currency, pickedSignedIn: currency });
  },
}));
